/**
 * The HOME's own filter rule, and the only place a comparison is on by default:
 * the home reads an absent `compare` as Previous period, so a member landing on
 * `/` sees every number against its prior window without asking. Every other
 * dashboard surface keeps the shared rule, where an absent `compare` is no
 * comparison at all.
 *
 * That default costs the URL a way to say "no comparison", which an absent
 * param can no longer mean: the home's search carries an explicit `none` token
 * instead. The default is the ROUTE's and is never saved — a member who chooses
 * None keeps None for that visit and that link, and the next visit compares
 * again.
 *
 * Everything but `compare` is the shared parser's, unchanged.
 */

import {
  EMPTY_FILTERS,
  type CompareMode,
  type DashboardSearch,
  type ViewerFilters,
  filtersFromSearch,
  parseDashboardSearch,
  searchFromFilters,
} from "./viewer-filters";

/** No period, no premises, Previous period — what Reset on the home restores. */
export const HOME_DEFAULT_FILTERS: ViewerFilters = { ...EMPTY_FILTERS, compare: "previous_period" };

/** The home URL's shape: the shared one, plus the token that says "no comparison". */
export type HomeSearch = Omit<DashboardSearch, "compare"> & { compare?: CompareMode | "none" };

const NONE = "none";

/**
 * The home route's `validateSearch` body. An unknown `compare` is DROPPED
 * rather than kept as a third state, so it reads as the default: a token
 * nothing wrote is not a choice the reader made.
 */
export function parseHomeSearch(raw: Record<string, unknown>): HomeSearch {
  const search: HomeSearch = parseDashboardSearch(raw);
  if (search.compare === undefined && raw.compare === NONE) search.compare = NONE;
  return search;
}

/** Absent → Previous period; `none` → no comparison; a mode → itself. */
export function homeFiltersFromSearch(search: HomeSearch): ViewerFilters {
  const filters = filtersFromSearch({ ...search, compare: undefined });

  if (search.compare === undefined) filters.compare = HOME_DEFAULT_FILTERS.compare;
  else if (search.compare !== NONE) filters.compare = search.compare;

  return filters;
}

/** The inverse: the default is OMITTED, no comparison writes `none`, a mode writes itself. */
export function homeSearchFromFilters(filters: ViewerFilters): HomeSearch {
  const search: HomeSearch = searchFromFilters({ ...filters, compare: undefined });

  if (filters.compare === undefined) search.compare = NONE;
  else if (filters.compare !== HOME_DEFAULT_FILTERS.compare) search.compare = filters.compare;

  return search;
}
